import React, { useState, useEffect, useCallback } from 'react';
import '../../assets/css/history.scss';
import { useGlobalPopup } from "../../contexts/GlobalPopupContext";
import { useTranslation } from "../../contexts/TranslationContext";
import api from '../../utils/api';
import HistoryFilter from './HistoryFilter';
import HistoryCard from './HistoryCard';
import ZoomModal from './ZoomModal';
import ProbeList from './ProbeList';
import PageTitle from '../UC/PageTitle';
import Loader from '../UC/Loader';
import Pagination from '../UC/Pagination';
import Page from '../UC/Page';

const History = () => {
  const { showPopup } = useGlobalPopup();
  const { language } = useTranslation();
  const [arrayProbeImages, setArrayProbeImages] = useState([]);
  const [historyData, setHistoryData] = useState([]);
  const [relatedAccounts, setRelatedAccounts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingFilters] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [isZoomOpen, setIsZoomOpen] = useState(false);
  const [zoomInitialIndex, setZoomInitialIndex] = useState(0);

  const [filters, setFilters] = useState({
    fromDate: '',
    toDate: '',
    recordCount: 100,
    agency: '',
    user: '',
    engineVersion: ''
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 50,
    totalRecords: 0,
    totalPages: 0
  });

  const svgIconHistory = (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
      <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
    </svg>
  );

  // Navigate to analyze page with specific ID
  const handleViewResults = (recordId) => {
    let currentUrl = window.location.href;
    currentUrl = currentUrl.slice(0, currentUrl.indexOf('/history'));
    const urlViewResults = `${currentUrl}/analyze/${recordId}`;
    window.open(`${urlViewResults}`, '_blank');
  };

  // Find probe image by Image_ID
  const findProbeImageById = (imageId) => {
    return arrayProbeImages.find(img =>
      img.Image_ID === imageId ||
      img.Image_ID === parseInt(imageId) ||
      img.ID === imageId ||
      img.ID === parseInt(imageId)
    );
  };

  // Handle zoom click from HistoryCard
  const handleZoomClick = useCallback((imageId) => {
    const idx = arrayProbeImages.findIndex(img =>
      img.Image_ID === imageId || img.ID === imageId ||
      img.Image_ID === parseInt(imageId) || img.ID === parseInt(imageId)
    );
    setZoomInitialIndex(idx >= 0 ? idx : 0);
    setIsZoomOpen(true);
  }, [arrayProbeImages]);

  // Fetch history data
  const fetchHistoryData = useCallback(async (page = 1) => {
    setIsLoading(true);

    try {
      const requestBody = {};

      const loginAccount = JSON.parse(localStorage.getItem("FWUser"));
      if (loginAccount?.Information?.AgencyType &&
          loginAccount.Information.AgencyType.toLowerCase() === "customer") {
        requestBody.Agency_IDs = [loginAccount.Information.User_ID];
      }
      else if (filters.agency) {
        requestBody.Agency_IDs = [parseInt(filters.agency)];
      }

      if (filters.user) {
        requestBody.User_IDs = [filters.user.toString()];
      }

      if (filters.fromDate) {
        const fromDate = new Date(filters.fromDate);
        const ticksFrom = (fromDate.getTime() * 10000) + 621355968000000000;
        requestBody.SearchTicksFrom = ticksFrom.toString();
      }

      if (filters.toDate) {
        const toDate = new Date(filters.toDate);
        const ticksTo = (toDate.getTime() * 10000) + 621355968000000000;
        requestBody.SearchTicksTo = ticksTo.toString();
      }

      let pageNumber = page || 1;
      let timezoneOffset = new Date().getTimezoneOffset();

      const response = await api.post(
        `${global.config.api.domain}/fwiq-api/api/search1n/history/search?numberOfRecordsPerPage=${pagination.pageSize}&pageNumber=${pageNumber}&timezoneoffset=${timezoneOffset}`,
        requestBody
      );

      const result = response.data;

      // Extract RelatedAccounts from the response if available
      if (result && result.RelatedAccounts && Array.isArray(result.RelatedAccounts)) {
        setRelatedAccounts(result.RelatedAccounts);
      } else {
        setRelatedAccounts([]);
      }

      // Handle the main data based on response structure
      let historyRecordsData = [];
      if (result && Array.isArray(result)) {
        historyRecordsData = result;
      } else if (result && result.Records && Array.isArray(result.Records)) {
        historyRecordsData = result.Records;
      } else if (result && result.Data && Array.isArray(result.Data)) {
        historyRecordsData = result.Data;
      }

      if (historyRecordsData.length > 0) {
        // Extract and format image data from records
        const allProbeImages = [];

        historyRecordsData.forEach((record, index) => {
          let imageOfProbe = result.ImageUrls.find(image => {
            return image.Image_ID === record.CustomFilters?.Image_ID
          });

          if (imageOfProbe) {
            const probeImage = {
              ...imageOfProbe, // Preserve S3RelativePath, S3OriginalRelativePath for ZoomModal
              Image_ID: record.CustomFilters?.Image_ID || `${record.ID}_${index}`,
              ID: record.CustomFilters?.Image_ID || `${record.ID}_${index}`,
              PublicPath: imageOfProbe.PublicPath,
              ImagePath: imageOfProbe,
              RelativePath: imageOfProbe.RelativePath,
              Make: record.Make || 'N/A',
              Model: record.Model || 'N/A',
              Type: record.Type || 'N/A'
            };
            allProbeImages.push(probeImage);
          }
          else if (record.CustomFilters?.Image_ID && !record.ImageUrls) {
            const probeImage = {
              Image_ID: record.CustomFilters.Image_ID,
              ID: record.CustomFilters.Image_ID,
              PublicPath: null,
              Make: record.Make || 'N/A',
              Model: record.Model || 'N/A',
              Type: record.Type || 'N/A'
            };
            allProbeImages.push(probeImage);
          }
        });

        setArrayProbeImages(allProbeImages);

        const totalRecords = result.TotalRecords || historyRecordsData.length;

        setHistoryData(historyRecordsData);
        setPagination(prev => ({
          ...prev,
          currentPage: page,
          totalRecords: result.TotalRecords || totalRecords,
          totalPages: result.TotalPages || Math.ceil(totalRecords / pagination.pageSize)
        }));
      } else {
        setHistoryData([]);
        setArrayProbeImages([]);
        setPagination(prev => ({
          ...prev,
          currentPage: 1,
          totalRecords: 0,
          totalPages: 0
        }));
      }
    } catch (error) {
      console.error('Error fetching history data:', error);

      showPopup({
        type: 'error',
        title: 'Error',
        content: 'Failed to load history data. Please try again.',
        options: [{ text: 'OK', action: null, type: 'default' }],
        showCloseButton: true,
        backdrop: true
      });

      setHistoryData([]);
    } finally {
      setIsLoading(false);
    }
  }, [filters]);

  // Load initial data
  useEffect(() => {
    fetchHistoryData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle filter changes
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle search
  const handleSearch = () => {
    fetchHistoryData(1);
  };

  // Handle reset filters
  const handleResetFilters = () => {
    setFilters({
      fromDate: '',
      toDate: '',
      recordCount: 100,
      agency: '',
      user: '',
      engineVersion: ''
    });
  };

  // Handle pagination
  const handlePageChange = (page) => {
    if (page >= 1 && page <= pagination.totalPages) {
      fetchHistoryData(page);
    }
  };

  // Handle page size change
  const handlePageSizeChange = (newPageSize) => {
    setPagination(prev => ({
      ...prev,
      pageSize: newPageSize,
      currentPage: 1
    }));
    fetchHistoryData(1);
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';

    try {
      if (typeof dateString === 'string' && dateString.length > 10 && /^\d+$/.test(dateString)) {
        const ticks = parseInt(dateString);
        const milliseconds = (ticks - 621355968000000000) / 10000;
        return new Date(milliseconds).toLocaleString();
      }
      return new Date(dateString).toLocaleString();
    } catch (error) {
      console.warn('Failed to parse date:', dateString, error);
      return dateString?.toString() || 'Invalid Date';
    }
  };

  // Page header content
  const pageHeaderContent = (
    <div className="header-content">
      <PageTitle
        title={language.history?.history || "History"}
        subtitle={language.history?.viewSystemActivity || "View system activity and audit logs"}
        icon={svgIconHistory}
      />
    </div>
  );

  // Sidebar filter content
  const sidebarFilterContent = (
    <HistoryFilter
      filters={filters}
      onFilterChange={handleFilterChange}
      onSearch={handleSearch}
      onReset={handleResetFilters}
      isLoading={isLoading}
      isLoadingFilters={isLoadingFilters}
    />
  );

  // Search result content
  const searchResultContent = (
    <>
      {isLoading ? (
        <Loader text={language.history?.loadingHistoryData || "Loading history data..."} />
      ) : historyData.length > 0 ? (
        <>
          {viewMode === 'list' ? (
            <ProbeList
              historyData={historyData}
              arrayProbeImages={arrayProbeImages}
              relatedAccounts={relatedAccounts}
              onViewResults={handleViewResults}
              formatDate={formatDate}
            />
          ) : viewMode === 'grid' ? (
            <div className="result-view grid-view">
              <div className="results grid">
                {historyData.map((record, index) => {
                  const probeImage = record.CustomFilters?.Image_ID ? findProbeImageById(record.CustomFilters.Image_ID) : null;

                  const accountAnalyze = relatedAccounts?.find((acc) => {
                    return acc.User_IDValue === record.User_ID && acc.User_ID_ExtValue === record.User_ID_Ext;
                  });

                  return (
                    <HistoryCard
                      key={record.ID || record.Id || index}
                      index={(pagination.currentPage - 1) * pagination.pageSize + index + 1}
                      record={record}
                      probeImage={probeImage}
                      accountAnalyze={accountAnalyze}
                      onViewResults={handleViewResults}
                      onZoomClick={handleZoomClick}
                    />
                  );
                })}
              </div>
            </div>
          ) : null}

          {/* Pagination */}
          {pagination.totalRecords > 0 && (
            <div className="result-summary">
              <Pagination
                totalRecords={pagination.totalRecords}
                pageIndex={pagination.currentPage}
                pageSize={pagination.pageSize}
                changePageIndex={(newPageIndex) => {
                  handlePageChange(newPageIndex);
                }}
                changePageSize={(newPageSize) => {
                  handlePageSizeChange(newPageSize);
                }}
              />
            </div>
          )}
        </>
      ) : (
        <div className="no-data">
          <i className="fa fa-history"></i>
          <h3>{language.history?.noHistoryDataFound || "No History Data Found"}</h3>
          <p>{language.history?.noActivityRecords || "No activity records match your current filters."}</p>
        </div>
      )}
    </>
  );

  return (
    <>
      <Page
        extentClassName="history"
        pageHeaderContent={pageHeaderContent}
        sidebarFilterContent={sidebarFilterContent}
        searchResultContent={searchResultContent}
      />
      <ZoomModal
        images={arrayProbeImages}
        initialIndex={zoomInitialIndex}
        isOpen={isZoomOpen}
        onClose={() => setIsZoomOpen(false)}
      />
    </>
  );
};

export default History;
